"use strict";
const _ = require("lodash");

class InputCheckboxControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("input-checkbox-wrapper");
        this._checkBoxEl = document.createElement("input-checkbox-control");
        this._rootElem.appendChild(this._checkBoxEl);
        this._checkBoxEl.tabIndex = 0;
        this._checkBoxEl.component = this._model;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._checkBoxEl.remove();
            this._checkBoxEl = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }

    // ["Disabled", "Value", "Visible", "Invalid", "DarkTheme", "Theme", "VariedChecked", "Label", "LabelShown", "Borderless", "Focus", "Blur", "UserValue", "UserFocus", "UserBlur", "UserKeyDown"]

    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("VariedChecked", (this._onVariedChecked = this._onVariedChecked.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("LabelShown", (this._onLabelShown = this._onLabelShown.bind(this)));
        this._model.on("Label", (this._onLabel = this._onLabel.bind(this)));
        this._model.on("LabelFirst", (this._onLabelFirst = this._onLabelFirst.bind(this)));
        this._model.on("Borderless", (this._onBorderless = this._onBorderless.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("Title", (this._onTitle = this._onTitle.bind(this)));
        this._checkBoxEl.addEventListener(
            "click",
            (this._onClickEvent = this._onClickEvent.bind(this))
        );
        this._checkBoxEl.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._checkBoxEl.addEventListener(
            "blur",
            (this._onBlurEvent = this._onBlurEvent.bind(this))
        );
        this._checkBoxEl.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("VariedChecked", this._onVariedChecked);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("LabelShown", this._onLabelShown);
        this._model.removeListener("Label", this._onLabel);
        this._model.removeListener("LabelFirst", this._onLabelFirst);
        this._model.removeListener("Borderless", this._onBorderless);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("Title", this._onTitle);
        this._checkBoxEl.removeEventListener("click", this._onClickEvent);
        this._checkBoxEl.removeEventListener("focus", this._onFocusEvent);
        this._checkBoxEl.removeEventListener("blur", this._onBlurEvent);
        this._checkBoxEl.removeEventListener("keydown", this._onKeyDownEvent);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            if (this._model._type === "toggle") {
                this._rootElem.classList.add("toggle");
                this._checkBoxEl.classList.add("toggle");
                this._labelEl = document.createElement("label");
                this._rootElem.appendChild(this._labelEl);
            } else {
                this._checkBoxEl.classList.add("default");
            }
            this._setDisabled(this._model.disabled);
            this._setVariedChecked(this._model.variedChecked);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setLabel(this._model.label);
            this._setLabelShown(this._model.labelShown);
            this._setLabelFirst(this._model.labelFirst);
            this._setBorderless(this._model.borderless);
            this._setTitle(this._model.title);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the shown state of the controls label has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onLabelShown(newVal) {
        this._setLabelShown(newVal);
        this._setLabelFirst(this._model.labelfirst);
    }

    /**
     * Fired when the text of the controls label has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onLabel(newVal) {
        this._setLabel(newVal);
        this._setLabelShown(this._model.isLabelShown());
        this._setLabelFirst(this._model.labelfirst);
    }

    /**
     * Fired when label first setting has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onLabelFirst(newVal) {
        this._setLabelFirst(newVal);
    }

    /**
     * Fired when the varied checked state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVariedChecked(newVal) {
        this._setVariedChecked(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {Boolean}
     * @param oldVal {Boolean}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when the borderless state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onBorderless(newVal) {
        this._setBorderless(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when setTitle has been called in the model
     * @private
     */
    _onTitle(newVal) {
        this._setTitle(newVal);
    }

    /**
     * If the control is not disabled, toggle the checked state
     * @param event {Event}
     * @private
     */
    _onClickEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        if (this._model._disabled) {
            event.stopImmediatePropagation();
        } else {
            let newVal = !this._model.value;
            this._model.variedChecked = false;
            this._model.setValue(newVal, true);
            this._model._triggerUserValue(newVal, oldVal, event);
        }
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._checkBoxEl.setAttribute("disabled", true);
            this._checkBoxEl.tabIndex = -1;
        } else {
            this._checkBoxEl.removeAttribute("disabled");
            this._checkBoxEl.tabIndex = 0;
        }
    }

    /**
     * Add/Remove the controls label
     * @param newVal {Boolean}
     * @private
     */
    _setLabelShown(newVal) {
        if (newVal === true) {
            this._checkBoxEl.classList.add("label-shown");
            this._setLabel(this._model.label);
        } else {
            this._checkBoxEl.classList.remove("label-shown");
            this._checkBoxEl.innerHTML = "";
        }
    }

    /**
     * Updates the controls label text
     * @param newVal {String}
     * @private
     */
    _setLabel(newVal) {
        if (this._model._type === "toggle") {
            this._labelEl.innerHTML = newVal;
        } else {
            this._checkBoxEl.innerHTML = newVal;
        }
    }

    _setLabelFirst(newVal) {
        if (newVal === true && this._model.isLabelShown()) {
            this._checkBoxEl.classList.add("label-first");
        } else {
            this._checkBoxEl.classList.remove("label-first");
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._checkBoxEl.classList.add("invalid");
        } else {
            this._checkBoxEl.classList.remove("invalid");
        }
    }

    /**
     * Toggle the varied checked state
     * @param newVal {Boolean}
     * @private
     */
    _setVariedChecked(newVal) {
        if (newVal) {
            this._checkBoxEl.classList.add("varied-checked");
        } else {
            this._checkBoxEl.classList.remove("varied-checked");
        }
    }

    /**
     * Toggle the value of the control
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        if (newVal) {
            this._checkBoxEl.classList.add("checked");
        } else {
            this._checkBoxEl.classList.remove("checked");
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._checkBoxEl.classList.remove("hidden");
        } else {
            this._checkBoxEl.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._checkBoxEl.classList.add("dark");
        } else {
            this._checkBoxEl.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._checkBoxEl.classList.remove(oldVal);
        }
        if (newVal) {
            this._checkBoxEl.classList.add(newVal);
        }
    }

    /**
     * Toggle or set the borderless state
     * @param newVal {Boolean}
     * @private
     */
    _setBorderless(newVal) {
        if (newVal === true) {
            this._checkBoxEl.classList.add("borderless");
        } else {
            this._checkBoxEl.classList.remove("borderless");
        }
    }

    /**
     * Set or remove focus on the control
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._checkBoxEl.focus();
        } else {
            this._checkBoxEl.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._checkBoxEl.setAttribute(attrName, attrValue);
        } else {
            this._checkBoxEl.removeAttribute(attrName);
        }
    }

    /**
     * Set title on the native control
     * @param newVal {String} - Title/tooltip to be set on the native control
     * @private
     */
    _setTitle(newVal) {
        if (newVal) {
            this._checkBoxEl.setAttribute("title", newVal);
        } else {
            this._checkBoxEl.setAttribute("title", "");
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = InputCheckboxControlRenderer;
